import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CurrencyPipe, PercentPipe } from '@angular/common';
import { TranslateLoader } from '@ngx-translate/core';
import { provideTranslateService } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideStore } from '@ngxs/store';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { AuthGuard } from '@capturum/auth';
import { provideCapturumAuth } from '@capturum/auth';
import { provideCapturumApi } from '@capturum/api';
import { FormBuilderModel } from '@capturum/builders/form-renderer';
import { provideCapturumLogin, LoginService } from '@capturum/login';
import { provideCapturumShared } from '@capturum/shared';
import {
  provideCapturumComplete,
  CapturumHttpCodeInterceptor,
  ErrorMessageInterceptor,
  PermissionGuard,
  TranslationLoader,
} from '@capturum/complete';
import { provideCapturumBuildersCore } from '@capturum/builders/core';
import { provideCapturumListRenderer } from '@capturum/builders/list-renderer';
import { provideCapturumFormRenderer } from '@capturum/builders/form-renderer';
import { provideCapturumFormly } from '@capturum/formly';
import { provideAngularSvgIcon } from 'angular-svg-icon';

import { routes } from './app-routes';
import LgPreset from './lg-theme.preset';
import { environment } from '@environments/environment';
import { SentryErrorHandler } from '@core/errorHandlers/sentry.errorHandler';
import CustomUrlSerializer from '@core/classes/custom-url-serializer.class';
import { API_DATE_BLACKLIST, API_DATE_PROPERTIES } from '@core/configs/app-date.config';
import { WINDOW } from '@core/configs/token.config';
import { AuthService } from '@core/services/auth.service';
import { BaseDataService } from '@core/services/base-data.service';
import { LGEAuthGuard } from '@core/guards/auth.guard';
import { LGELoginService } from '@core/services/login.service';
import { SESSION_EXPIRED_FLAG } from '@core/constants/session-expired.constant';
import { EndpointAction } from '@core/actions/endpoint.action';
import { EXPORT_POLLING, UPLOAD_POLLING } from '@core/provider/polling.provider';
import { InstallersState } from '@store/general/installers.state';
import { PollingHandlerService } from './features/leads/services/polling-handler.service';
import { UploadLeadsComponent } from './features/leads/components/upload-leads/upload-leads.component';
import { UploadInstallersComponent } from './features/installers/components/upload-installers/upload-installers.component';
import { InstallerCategoryWidgetComponent } from '@shared/dumb-components/installer-category-widget/installer-category-widget.component';
import { InstallerOverallRatingWidgetComponent } from '@shared/dumb-components/installer-overall-rating-widget/installer-overall-rating-widget.component';
import { LeadCommentsWidgetComponent } from '@shared/dumb-components/lead-comments-widget/lead-comments-widget.component';
import { InstallerQuotesSentComponent } from '@shared/dumb-components/installer-quotes-sent/installer-quotes-sent.component';
import { ColoredColumnByStatusComponent } from '@shared/dumb-components/colored-column/colored-column-by-status.component';
import { InstallerCategoryDropdownComponent } from '@shared/modules/formly-types/types/installer-category-dropdown/installer-category-dropdown.component';
import { DatepickerComponent } from '@shared/modules/formly-types/types/datepicker/datepicker.component';
import { FORMLY_TYPES_MODULE, FormlyTypesModule } from '@shared/modules/formly-types/formly-types.module';
import { SharedModule } from '@shared/shared.module';
import { UserAgreementModule } from './features/user-agreement/user-agreement.module';

function onAuthError(): void {
  // Survives the hard reload below — LgLoginComponent reads it on init
  // to surface the session-expired toast.
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  window.location.href = `auth/login`;
}

export function errorHandlerFactor(): ErrorHandler {
  if (environment.production) {
    return new SentryErrorHandler();
  } else {
    return new ErrorHandler();
  }
}

function windowFactory(): Window {
  return window;
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Router
    provideRouter(routes),

    // HTTP client with class-based interceptor support
    provideHttpClient(withInterceptorsFromDi()),

    // Animations
    provideAnimationsAsync(),

    // Permissions (must be before Capturum Complete)
    importProvidersFrom(NgxPermissionsModule.forRoot()),

    // Capturum packages
    provideCapturumAuth({
      baseUrl: environment.baseUrl,
      production: environment.production,
      authRejectionRoute: 'auth/login',
      unauthenticatedRejectionRoute: '',
      authService: AuthService as any,
      authRefreshToken: {
        useRefreshToken: true,
        offsetSeconds: 60,
        intervalSeconds: 3,
      },
    }),

    provideCapturumApi({
      baseUrl: environment.baseUrl,
      production: environment.production,
      onAuthError,
      dateInterceptor: {
        dateProperties: API_DATE_PROPERTIES,
        blackListedURLs: API_DATE_BLACKLIST,
        blackListedStatusCodes: [401, 204],
        displayFormat: 'dd-MM-yyyy HH:mm:ss',
        sendFormat: 'yyyy-MM-dd HH:mm:ss',
      },
    }),

    provideCapturumLogin({
      environment,
      defaultLanguage: 'en',
      productName: 'LG | Lead Platform',
      backgroundImageUrl: 'assets/images/background.png',
      logoImageUrl: 'assets/images/logo_with_text.png',
      displayPassword: true as any,
    }),

    provideCapturumShared(),

    provideCapturumComplete({
      indexedDbModels: [FormBuilderModel],
      databaseName: 'lge',
      userSpecificDb: false,
      version: 1,
      environment,
      loaderText: 'Loading',
    }),

    // Translations
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoader,
      },
    }),

    // NGXS State Management
    provideStore([InstallersState], withNgxsStoragePlugin({ keys: [InstallersState] }), withNgxsReduxDevtoolsPlugin()),

    // Capturum Builders
    provideCapturumBuildersCore({
      baseUrl: 'builders',
      actions: {
        endpoint: EndpointAction,
      },
      components: {
        list_leads_import_leads: UploadLeadsComponent,
        list_installers_import_installers: UploadInstallersComponent,
      },
    }),

    provideCapturumListRenderer({
      widgets: {
        'installer-category-widget': InstallerCategoryWidgetComponent,
        'installer-overall-rating-widget': InstallerOverallRatingWidgetComponent,
        'lead-comments-widget': LeadCommentsWidgetComponent,
        'installer-quotes-sent': InstallerQuotesSentComponent,
        'colored-column-by-status': ColoredColumnByStatusComponent,
      },
    }),

    provideCapturumFormRenderer({
      translationKeyPrefix: '',
      types: [
        {
          name: 'installercategorydropdown',
          component: InstallerCategoryDropdownComponent,
        },
        {
          name: 'customdateinput',
          component: DatepickerComponent,
        },
      ],
    }),

    // Formly (Capturum base types + app custom types)
    provideCapturumFormly(),
    importProvidersFrom(FORMLY_TYPES_MODULE),
    importProvidersFrom(FormlyTypesModule),

    // SharedModule (needed for root route components like BasicLayoutComponent, LoginLayoutComponent)
    importProvidersFrom(SharedModule),

    // UserAgreementModule (component used directly in routes)
    importProvidersFrom(UserAgreementModule),

    // SVG icons
    provideAngularSvgIcon(),

    // PrimeNG theme
    providePrimeNG({
      theme: {
        preset: LgPreset,
        options: {
          darkModeSelector: false,
          cssLayer: false,
        },
      },
    }),

    // HTTP Interceptors (order matters: CapturumHttpCodeInterceptor first, then ErrorMessageInterceptor)
    CapturumHttpCodeInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CapturumHttpCodeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorMessageInterceptor,
      multi: true,
    },

    // Service overrides
    {
      provide: AuthGuard,
      useClass: LGEAuthGuard,
    },
    {
      provide: LoginService,
      useClass: LGELoginService,
    },

    // Individual providers
    {
      provide: UPLOAD_POLLING,
      useClass: PollingHandlerService,
    },
    {
      provide: EXPORT_POLLING,
      useClass: PollingHandlerService,
    },
    ConfirmationService,
    EndpointAction,
    PermissionGuard,
    BaseDataService,
    CurrencyPipe,
    PercentPipe,
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
    { provide: ErrorHandler, useFactory: errorHandlerFactor },
    { provide: WINDOW, useFactory: windowFactory },
  ],
};
